import {ReactElement, useEffect, useRef, useState} from "react";
import {
    GetPmsOccupiedUnitCountsQuery,
    UnitTypeScheduleValueUpsertInput,
    useGetPmsOccupiedUnitCountsQuery,
    VersionType
} from "../../../../__generated__/generated_types";
import {toast} from "react-toastify";
import {Property} from "../../../../contexts/properties/logic";
import {UnitTypeSyncWithPMSTable} from "../shared-sync-with-pms/UnitTypeSyncWithPMSTable";
import {useSaveUnitTypeScheduleValues} from "../base";
import {TUnitTypeData} from "../shared-sync-with-pms/logic";


export interface OccupancyRatesRewriteViewProps {
    property: Property,
    budgetYear: number,
    valuesStartFromIndex: number,
    unitTypes: {id: string, type: string;}[];
    versionType: VersionType,
    onCancel: () => void,
    onApply: () => void;
}

function ratio(numerator: number | undefined | null, denominator: number | undefined | null): number | null {
    if(numerator !== null && numerator !== undefined
        && denominator !== null && denominator !== undefined
        && Math.abs(denominator) > 0.001
    ) {
        return numerator / denominator;
    }

    return null;
}

function toPercent(val: number | null): number | null {
    if(val == null) {
        return null;
    }
    return +(Math.round(val * 10000) / 100).toFixed(2);
}

function buildUnitTypesData(
    rawData: GetPmsOccupiedUnitCountsQuery,
    valuesStartFromIndex: number,
    unitTypes: {id: string, type: string;}[],
    versionType: VersionType
): TUnitTypeData[] {
    const forecastedValuesByUnitType: Record<string, (number | null)[]> = {};
    for(const utsv of rawData.valueVersions.find(vv => vv.type == versionType)?.unitTypeScheduleValues ?? []) {
        let utValuesSoFar = forecastedValuesByUnitType[utsv.unitTypeId];
        if(!utValuesSoFar) {
            utValuesSoFar = new Array<number | null>(12).fill(null);
            forecastedValuesByUnitType[utsv.unitTypeId] = utValuesSoFar;
        }
        const val = parseFloat(utsv.occupancyRate);
        if(isNaN(val)) {
            utValuesSoFar[utsv.monthIndex] = null;
        }
        else {
            utValuesSoFar[utsv.monthIndex] = val;
        }
    }
    const pmsUnitCountsByUnitTypeId = rawData.queryPMSUnitCountsForPropertyAndYears.toIdMap("unitTypeId");
    const pmsOccupiedUnitCountsByUnitTypeId = rawData.queryPMSOccupiedUnitCountsForPropertyAndYears.toIdMap("unitTypeId");
    const ret = [] as TUnitTypeData[];
    for(const unitType of unitTypes) {
        const pmsUnitCounts = pmsUnitCountsByUnitTypeId[unitType.id];
        const pmsOccupiedUnitCounts = pmsOccupiedUnitCountsByUnitTypeId[unitType.id];
        const pmsValues = new Array<number | null>(12).fill(null);
        if(pmsUnitCounts && pmsOccupiedUnitCounts) {
            for(let i = valuesStartFromIndex; i < 12; i++) {
                const unitCount = pmsUnitCounts.values[i];
                const occupiedCount = pmsOccupiedUnitCounts.values[i];
                pmsValues[i] = toPercent(ratio(occupiedCount, unitCount));
            }
        }
        const forecastedValues = new Array<number | null>(12).fill(null);
        const utForecastedValues = forecastedValuesByUnitType[unitType.id];
        if(utForecastedValues) {
            for(let i = valuesStartFromIndex; i < 12; i++) {
                const utValue = utForecastedValues[i];
                if(utValue !== null && utValue !== undefined) {
                    forecastedValues[i] = toPercent(utValue);
                }
            }
        }
        ret.push({
            unitTypeId: unitType.id,
            unitTypeName: unitType.type,
            pmsValues: [...pmsValues],
            currentForecastedValues: [...forecastedValues],
            newForecastedValues: [...pmsValues],
            useNewForecasedValues: true
        });
    }

    return ret;
}

export function OccupancyRatesRewriteView(props: OccupancyRatesRewriteViewProps): ReactElement {
    const {data: occupiedCountsRawData, loading: occupiedCountsRawLoading} = useGetPmsOccupiedUnitCountsQuery({
        variables: {
            propertyId: props.property.id,
            propertyIdStr: props.property.id,
            pmsDataYears: [props.versionType == VersionType.Reforecast ? props.budgetYear - 1 : props.budgetYear],
            budgetYear: props.budgetYear
        },
        fetchPolicy: "no-cache"
    });
    const saveChanges = useSaveUnitTypeScheduleValues(props.property.id, "occupancyRate", props.budgetYear);
    const isSaving = useRef(false);

    const [unitTypesData, setUnitTypesData] = useState<TUnitTypeData[]>();

    useEffect(() => {
        if(!occupiedCountsRawData || occupiedCountsRawLoading) {
            return;
        }

        const unitTypesData = buildUnitTypesData(occupiedCountsRawData, props.valuesStartFromIndex, props.unitTypes, props.versionType);
        setUnitTypesData(unitTypesData.sortBy("unitTypeName"));
    }, [occupiedCountsRawData, occupiedCountsRawLoading]);

    function handleApply(updates: {
        unitTypeId: string;
        values: (number | null | undefined)[];
    }[]) {
        if(!occupiedCountsRawData || !unitTypesData) {
            return;
        }
        const unitTypeNameById = unitTypesData.toIdMap("unitTypeId", "unitTypeName");
        const versionId = occupiedCountsRawData.valueVersions.find(v => v.type == props.versionType)?.id;
        if(!versionId) {
            return;
        }
        const values: UnitTypeScheduleValueUpsertInput[] = [];
        for(const update of updates) {
            const utName = unitTypeNameById[update.unitTypeId];
            if(!utName) {
                continue;
            }
            update.values.forEach((val, monthIndex) => {
                if(val !== undefined && val !== null) {
                    values.push({monthIndex, unitTypeName: utName, value: (val / 100.0).toString()});
                }
            });
        }
        isSaving.current = true;
        saveChanges(
            versionId,
            values
        )
            .then(() => toast.success(`Updated Successfully`))
            .catch(() => toast.error(`Failed To Update`))
            .finally(() => {isSaving.current = true; props.onApply()});
    }

    return (
        <UnitTypeSyncWithPMSTable
            data={unitTypesData}
            valuesStartFromIndex={props.valuesStartFromIndex}
            isApplying={isSaving.current}
            onCancel={props.onCancel}
            onApply={handleApply}
        />
    );
}